import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

export interface DropdownOption<T = any> {
  value: T;
  label: string;
  disabled?: boolean;
}

export interface DropdownProps<T = any> {
  options: DropdownOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  buttonClassName?: string;
  menuClassName?: string;
  menuWidth?: string;
  renderButton?: (
    option: DropdownOption<T> | undefined,
    isOpen: boolean,
    buttonRef: React.RefObject<HTMLButtonElement | null>,
    toggleOpen: () => void
  ) => React.ReactNode;
  renderOption?: (option: DropdownOption<T>, isSelected: boolean) => React.ReactNode;
  getButtonStyle?: (option: DropdownOption<T> | undefined) => React.CSSProperties;
  getOptionStyle?: (option: DropdownOption<T>, isSelected: boolean) => React.CSSProperties;
  placeholder?: string;
  disabled?: boolean;
}

export default function Dropdown<T = any>({
  options,
  value,
  onChange,
  className = "",
  buttonClassName = "",
  menuClassName = "",
  menuWidth = "160px",
  renderButton,
  renderOption,
  getButtonStyle,
  getOptionStyle,
  placeholder = "انتخاب کنید",
  disabled = false,
}: DropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  const currentOption = options.find(
    (opt) => opt.value === value
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Calculate menu position
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setMenuPosition({
          top: rect.bottom + window.scrollY + 4,
          right: window.innerWidth - rect.right + window.scrollX,
        });
      }
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleOptionClick = (option: DropdownOption<T>) => {
    if (option.disabled) return;
    
    if (option.value !== value) {
      onChange(option.value);
    }
    setIsOpen(false);
  };

  const defaultButtonStyle: React.CSSProperties = getButtonStyle
    ? getButtonStyle(currentOption)
    : {};

  const defaultRenderButton = () => (
    <button
      ref={buttonRef}
      type="button"
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) {
          setIsOpen(!isOpen);
        }
      }}
      className={`text-xs inline-flex items-center justify-center py-1 px-3 rounded-2xl border-0 outline-none cursor-pointer transition-all duration-200 hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed ${buttonClassName}`}
      style={defaultButtonStyle}
    >
      <span className="px-2">{currentOption?.label || placeholder}</span>
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`transition-transform duration-200 ${
          isOpen ? "rotate-180" : ""
        }`}
        style={{ fill: defaultButtonStyle.color || "currentColor" }}
      >
        <path d="M6 9L1 4h10z" />
      </svg>
    </button>
  );

  const defaultRenderOption = (option: DropdownOption<T>, isSelected: boolean) => {
    const optionStyle: React.CSSProperties = getOptionStyle
      ? getOptionStyle(option, isSelected)
      : {};

    return (
      <button
        key={String(option.value)}
        type="button"
        disabled={option.disabled}
        onClick={(e) => {
          e.stopPropagation();
          handleOptionClick(option);
        }}
        className={`w-full text-right px-3 py-2 text-xs transition-all duration-150 ${
          isSelected
            ? "font-semibold"
            : "font-normal text-gray-700 hover:bg-gray-50"
        } ${
          option.disabled ? "opacity-50 cursor-not-allowed" : ""
        } ${menuClassName}`}
        style={optionStyle}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="truncate">{option.label}</span>
          {isSelected && (
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="flex-shrink-0"
              style={{ fill: optionStyle.color || "currentColor" }}
            >
              <path
                d="M13.5 4.5L6 12L2.5 8.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          )}
        </div>
      </button>
    );
  };

  return (
    <>
      <div
        ref={dropdownRef}
        className={`relative ${className}`}
        onClick={(e) => e.stopPropagation()}
        style={renderButton ? { width: "100%", height: "100%" } : undefined}
      >
        {renderButton
          ? renderButton(currentOption, isOpen, buttonRef, () => setIsOpen(!isOpen))
          : defaultRenderButton()}
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-[9999] bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden"
            style={{
              top: `${menuPosition.top}px`,
              right: `${menuPosition.right}px`,
              boxShadow:
                "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
              width: menuWidth,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {options.map((option, index) => {
              const isSelected = option.value === value;
              return (
                <div
                  key={String(option.value)}
                  className={
                    index !== options.length - 1
                      ? "border-b border-gray-100"
                      : ""
                  }
                >
                  {renderOption
                    ? renderOption(option, isSelected)
                    : defaultRenderOption(option, isSelected)}
                </div>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
};